<template>
  <div
    class="grid grid-flow-row-dense grid-cols-1 mobile:grid-cols-[4em_1fr] laptop:grid-cols-[1fr_4em_1fr] gap-x-16"
  >
    <h2 class="sr-only">
      {{ props.title }}
    </h2>
    <template
      v-for="(item, index) in props.items"
      :key="item.id || index"
    >
      <!-- icon and divider -->
      <div
        class="hidden mobile:flex flex-col gap-y-5 laptop:col-start-2"
      >
        <span
          class="flex-1 min-h-[1em] mx-auto"
          :class="{
            'line line-rounded-b': !first(index),
          }"
        />
        <div
          class="flex bg-primary text-white rounded-lg w-[4em] aspect-square"
        >
          <font-awesome-icon
            class="m-auto text-[2em]"
            :icon="item.icon"
          />
        </div>
        <span
          class="flex-1 line-rounded-t min-h-[1em] mx-auto"
          :class="{
            'line': !last(index),
            'max-laptop:line': last(index),
          }"
        />
      </div>

      <!-- left -->
      <div
        class="flex-1 flex my-[1em]"
        :class="{
          'laptop:col-start-3': odd(index) && props.alternating,
        }"
      >
        <slot
          name="left"
          :content="item.content"
          :index="index"
        />
      </div>

      <!-- extra line next to `right` when in 2-column-mode -->
      <span
        class="flex-1 line min-h-[2em] mx-auto hidden mobile:flex laptop:hidden"
        :class="{
          'line-rounded-b': last(index),
        }"
      />

      <!-- right (in next line when in 2-column-mode) -->
      <div
        class="flex-1 flex mt-[2em] mb-[4em] laptop:my-[1em]"
      >
        <slot
          name="right"
          :content="item.content"
          :index="index"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { Icon } from '~/utils/types'

interface TreeItem {
  id?: string
  icon: Icon
  content: any
}

const props = defineProps<{
  title: string
  items: TreeItem[]
  alternating?: boolean // only affects 3-column-mode
}>()

// checks if the index is the first index in the tree
const first = (index: number): boolean => {
  return index === 0
}
// checks if the index is the last index in the tree
const last = (index: number): boolean => {
  return index === (props.items.length - 1)
}
// checks if the index is even
const odd = (index: number): boolean => {
  return index % 2 !== 0
}
</script>

<style lang="postcss" scoped>
/* add pseudo selector for line (for better readability) */
.max-laptop\:line {
  @apply max-laptop:w-1.5 max-laptop:h-full max-laptop:bg-[#BEBEBE]
}

.line {
  @apply w-1.5 h-full bg-[#BEBEBE]
}

.line-rounded-t {
  @apply rounded-t-lg
}

.line-rounded-b {
  @apply rounded-b-lg
}
</style>
